import { ArrowUpRight, Check } from "lucide-react";
import { computeLineSubtotalPaise } from "./bill-math.js";
import type { BillTotals, NewBillFormValues, VendorOption } from "./types.js";
import { paiseToInrExact } from "../../../lib/format.js";

interface BillPreviewProps {
  values: NewBillFormValues;
  vendor: VendorOption | null;
  totals: BillTotals;
}

/**
 * Live preview of the bill being entered, styled as a printable tax invoice.
 *
 * Layout matches the design partner's reference: large vendor block on the
 * left + "TAX INVOICE" / mono doc number / "Auto-extracted" pill on the right;
 * a single hairline divider; 2-column meta grid (Bill to / Bill date + Due /
 * PO ref / GRN); item table with HSN + GST inline on the item name; totals
 * stack right-aligned at the bottom; TDS line in rose.
 */
export function BillPreview({ values, vendor, totals }: BillPreviewProps) {
  const halfTax = totals.taxPaise / 2n;
  const showExtracted = values.extractionState === "extracted";
  const tdsPaise = computeTdsPaise(totals.subtotalPaise, values.tdsRate);
  const taxRows = previewTaxRows(values.taxMode, halfTax, totals.taxPaise);

  return (
    <aside className="sticky top-20 self-start overflow-hidden rounded-md border border-line bg-paper shadow-sm">
      {/* Header strip — "Live preview" + Open original */}
      <header className="flex items-center justify-between gap-3 border-b border-line px-5 py-3.5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
          <span className="h-2 w-2 rounded-full bg-emerald-500" />
          Live preview
        </h2>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs font-medium text-muted hover:text-ink-2"
        >
          Open original
          <ArrowUpRight className="h-3 w-3" />
        </button>
      </header>

      {/* Invoice header — vendor block / TAX INVOICE label */}
      <div className="flex items-start justify-between gap-6 px-5 pb-4 pt-5">
        <div className="min-w-0">
          <p className="text-base font-bold tracking-tight text-ink">
            {vendor?.name ?? "Western Industrial"}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-muted">
            Plot 23, MIDC Andheri,
            <br />
            Mumbai 400093
            <br />
            <span className="text-muted">GSTIN </span>
            <span className="font-mono">{vendor?.gstin ?? "27AAAWI3344L1Z9"}</span>
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted">
            Tax invoice
          </p>
          <p className="mt-1 font-mono text-base font-bold text-ink">
            {values.vendorDocNumber || "WI-2026-0412"}
          </p>
          {showExtracted ? (
            <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-emerald-700">
              <Check className="h-2.5 w-2.5" />
              Auto-extracted
            </span>
          ) : null}
        </div>
      </div>

      <div className="border-t border-line" />

      {/* Meta — 2-col grid, Bill to / Bill date + Due / PO ref / GRN */}
      <div className="grid grid-cols-2 gap-x-8 gap-y-4 px-5 py-4">
        <MetaCell label="Bill to">
          <p className="font-semibold text-ink">Sharma Industries Pvt Ltd</p>
          <p className="mt-0.5 text-muted">Andheri MIDC, Mumbai</p>
          <p className="font-mono text-muted">27AABCS1234E1Z5</p>
        </MetaCell>
        <MetaCell label="Bill date">
          <p className="font-semibold text-ink">{formatDate(values.receivedOn)}</p>
        </MetaCell>
        <MetaCell label="Po ref">
          <p className="font-mono text-ink">{values.poNumber || "—"}</p>
        </MetaCell>
        <MetaCell label="Due">
          <p className="text-ink">
            <span className="font-semibold">{formatDate(values.dueOn)}</span>
            <span className="ml-1 text-muted">({termsLabel(values.paymentTerms)})</span>
          </p>
        </MetaCell>
        <MetaCell label="Grn">
          <p className="font-mono text-ink">
            {values.grnNumber || "—"}
            {values.grnNumber ? <span className="text-muted"> · 02 May</span> : null}
          </p>
        </MetaCell>
      </div>

      <div className="border-t border-line" />

      {/* Items */}
      <div className="px-5 pt-4">
        <div className="grid grid-cols-[1fr_3rem_4rem_5rem] gap-3 text-xs font-semibold uppercase tracking-wider text-muted">
          <span>Item</span>
          <span className="text-right">Qty</span>
          <span className="text-right">Rate</span>
          <span className="text-right">Amount</span>
        </div>
        <div className="mt-2 divide-y divide-line">
          {(values.lineItems ?? []).map((row) => {
            const label = shortDescription(row.description);
            return (
              <div
                key={row.id}
                className="grid grid-cols-[1fr_3rem_4rem_5rem] items-center gap-3 py-2 text-xs"
              >
                <div className="min-w-0">
                  <span className="text-ink">{label || "—"}</span>
                  {row.hsn || row.taxRate ? (
                    <span className="ml-2 text-xs text-muted">
                      {row.hsn ? <>HSN {row.hsn}</> : null}
                      {row.hsn && row.taxRate ? " · " : null}
                      {row.taxRate ? <>{row.taxRate}% GST</> : null}
                    </span>
                  ) : null}
                </div>
                <span className="text-right font-mono text-ink">{row.qty || "—"}</span>
                <span className="text-right font-mono text-ink">{row.rate || "—"}</span>
                <span className="text-right font-mono text-ink">
                  {paiseToInrExact(computeLineSubtotalPaise(row)).replace("₹", "")}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Totals — right-aligned, single column */}
      <div className="border-t border-line px-5 py-4">
        <dl className="ml-auto w-full max-w-xs space-y-1.5 text-xs">
          <TotalRow
            label="Subtotal"
            value={paiseToInrExact(totals.subtotalPaise).replace("₹", "")}
          />
          {taxRows.map((row) => (
            <TotalRow key={row.label} label={row.label} value={row.value} />
          ))}
          <TotalRow label="Round-off" value="0.00" />
          <div className="border-t border-line pt-2">
            <TotalRow
              label={<span className="text-sm font-semibold text-ink">Bill total</span>}
              value={
                <span className="text-sm font-semibold text-ink">
                  {paiseToInrExact(totals.totalPaise)}
                </span>
              }
            />
          </div>
          <TotalRow
            label={<span className="text-rose-600">TDS @ {values.tdsRate || "0"}%</span>}
            value={
              <span className="font-mono text-rose-600">
                -{paiseToInrExact(tdsPaise).replace("₹", "")}
              </span>
            }
          />
        </dl>
      </div>

      <div className="flex items-center gap-1.5 border-t border-line bg-bg/40 px-5 py-2.5 text-xs font-medium text-emerald-700">
        <Check className="h-3 w-3" />
        {taxModePreview(values.taxMode)} · {values.rcmApplies ? "RCM applies" : "No RCM"} ·{" "}
        {values.itcEligible ? "ITC eligible" : "ITC blocked"}
      </div>
    </aside>
  );
}

function MetaCell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted">{label}</p>
      <div className="mt-1 text-xs leading-relaxed">{children}</div>
    </div>
  );
}

function TotalRow({ label, value }: { label: React.ReactNode; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-muted">{label}</dt>
      <dd className="font-mono text-ink">{value}</dd>
    </div>
  );
}

function shortDescription(value: string): string {
  if (!value) return "";
  const head = value.split(" · ")[0] ?? value;
  // Trim brand/material noise so the preview reads like a real tax invoice.
  return head.replace(/\s+(galvanised|zinc-plated|stainless|DIN 912).*/i, "").trim();
}

function termsLabel(payment: string | undefined): string {
  if (!payment) return "net";
  if (/^net\s+\d+/i.test(payment)) return payment.toLowerCase().replace(/\s+/, "-");
  return payment.toLowerCase();
}

function computeTdsPaise(basePaise: bigint, rate: string): bigint {
  const bps = parsePercentBps(rate);
  return bps === null ? 0n : (basePaise * bps) / 10000n;
}

function parsePercentBps(value: string): bigint | null {
  const cleaned = value.trim();
  if (!/^\d+(\.\d{0,2})?$/.test(cleaned)) return null;
  const [whole = "0", fraction = ""] = cleaned.split(".");
  return BigInt(whole || "0") * 100n + BigInt((fraction + "00").slice(0, 2));
}

function taxModePreview(mode: NewBillFormValues["taxMode"]): string {
  if (mode === "inter") return "Inter-state · IGST";
  if (mode === "exempt") return "Exempt / non-GST";
  return "Intra-state · CGST + SGST";
}

function previewTaxRows(
  mode: NewBillFormValues["taxMode"],
  halfTaxPaise: bigint,
  taxPaise: bigint,
): Array<{ label: string; value: string }> {
  if (mode === "inter") {
    return [{ label: "IGST 18%", value: paiseToInrExact(taxPaise).replace("₹", "") }];
  }
  if (mode === "exempt") {
    return [{ label: "GST", value: "0.00" }];
  }
  return [
    { label: "CGST 9%", value: paiseToInrExact(halfTaxPaise).replace("₹", "") },
    { label: "SGST 9%", value: paiseToInrExact(halfTaxPaise).replace("₹", "") },
  ];
}

function formatDate(iso: string | undefined): string {
  if (!iso) return "—";
  const [year, month, day] = iso.split("-");
  if (!year || !month || !day) return iso;
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const monthLabel = months[Number(month) - 1] ?? month;
  return `${day} ${monthLabel} ${year}`;
}
